import pdfMake from 'pdfmake/build/pdfmake'
import * as pdfFonts from 'pdfmake/build/vfs_fonts'
import type { Content, TDocumentDefinitions } from 'pdfmake/interfaces'
import logo from '/logo.png'
import { urlToDataURL } from '@/pages/Patient/components/OrderTests/pdf/pdfHelpers'
import { logError } from '@/utils/errorUtils'

const vfs =
  (pdfFonts as Record<string, unknown>).vfs ||
  (pdfFonts as Record<string, Record<string, unknown>>).pdfMake?.vfs
if (vfs) {
  pdfMake.vfs = vfs as Record<string, string>
}

const LOGO_WIDTH = 60

export const textToPdf = async (text: string, downloadName: string) => {
  const normalized = text.replace(/\t/g, '    ')
  const logoDataUrl = await urlToDataURL(logo)

  pdfMake
    .createPdf({
      pageSize: 'A4',
      pageMargins: [36, 36, 36, 42],
      defaultStyle: { fontSize: 12, lineHeight: 1.3 },
      images: {
        logo: logoDataUrl,
      },
      content: [
        { image: 'logo', width: 80, alignment: 'center' },
        {
          text: normalized,
          preserveLeadingSpaces: true,
          noWrap: false,
          marginTop: 30,
        },
      ],
    })
    .download(`${downloadName}.pdf`)
}

type CCDATable = {
  title: string
  textContent: string
  textElement: Element
}

type DocumentMetadata = {
  title: string
  source: string
  type: string
  documentName: string
  provider: string
  fetchedOn: string
  modifiedOn: string
  patientName: string
  patientDob: string
}

export const ccdaToPdf = async (
  metadata: DocumentMetadata,
  ccdaTables: CCDATable[],
  textHtml: string[],
  /** "Electronically signed by …" for a signed Progress Note (PD-1897). */
  signature?: string,
): Promise<void> => {
  // pdfmake throws on an empty image node, so drop the logo rather than the export.
  let logoDataUrl = ''
  try {
    logoDataUrl = await urlToDataURL(logo)
  } catch (err) {
    logError('patient', 'Document PDF logo', err)
  }

  const content: Content[] = [] as Content[]

  const title: Content = { text: metadata.title, style: 'header', alignment: 'center' }
  content.push(
    logoDataUrl
      ? {
          columns: [
            { width: LOGO_WIDTH, text: '' },
            { ...title, width: '*' },
            { image: logoDataUrl, width: LOGO_WIDTH, margin: [0, 4, 0, 0] },
          ],
          margin: [0, 0, 0, 10],
        }
      : { ...title, margin: [0, 0, 0, 10] },
    { text: '\n' },
  )

  const metadataContent = [
    { text: 'Document Information', style: 'subheader', margin: [0, 0, 0, 10] },
    {
      columns: [
        {
          width: '50%',
          stack: [
            { text: [{ text: 'Patient: ', bold: true }, metadata.patientName] },
            { text: [{ text: 'Source: ', bold: true }, metadata.source] },
            { text: [{ text: 'Type: ', bold: true }, metadata.type] },
            { text: [{ text: 'Document Name: ', bold: true }, metadata.documentName] },
            { text: [{ text: 'Provider: ', bold: true }, metadata.provider] },
          ],
        },
        {
          width: '50%',
          stack: [
            { text: [{ text: 'Date of Birth: ', bold: true }, metadata.patientDob] },
            { text: [{ text: 'Added on: ', bold: true }, metadata.fetchedOn] },
            { text: [{ text: 'Modified on: ', bold: true }, metadata.modifiedOn] },
          ],
        },
      ],
    },
    { text: '\n' },
  ]
  content.push(...(metadataContent as Content[]))

  if (ccdaTables.length > 0) {
    ccdaTables.forEach((table) => {
      content.push({ text: table.title, style: 'tableHeader', margin: [0, 10, 0, 5] })

      const tableElements = table.textElement.querySelectorAll('table')

      tableElements.forEach((htmlTable) => {
        const pdfTable: { body: unknown[][] } = { body: [] }

        let allRows: HTMLTableRowElement[] = []
        const thead = htmlTable.querySelector('thead')
        if (thead) {
          allRows.push(...Array.from(thead.querySelectorAll('tr')))
        }
        const tbody = htmlTable.querySelector('tbody')
        if (tbody) {
          allRows.push(...Array.from(tbody.querySelectorAll('tr')))
        }
        if (!thead && !tbody) {
          allRows = Array.from(htmlTable.querySelectorAll('tr'))
        }

        let maxCols = 0
        const rowDataArr: unknown[][] = []
        allRows.forEach((row, rowIdx) => {
          const cells = row.querySelectorAll('th, td')
          const rowData: unknown[] = Array.from(cells).map((cell) => {
            const text = cell.textContent?.trim() || ''
            if (rowIdx === 0) {
              return { text, bold: true, fillColor: '#f0f0f0' }
            }
            return text
          })
          if (rowData.length > maxCols) maxCols = rowData.length
          rowDataArr.push(rowData)
        })
        rowDataArr.forEach((row) => {
          while (row.length < maxCols) row.push('')
        })
        pdfTable.body = rowDataArr

        if (pdfTable.body.length > 0) {
          content.push({
            table: pdfTable,
            margin: [0, 5, 0, 10],
            layout: 'lightHorizontalLines',
          } as Content)
        }
      })

      if (tableElements.length === 0) {
        const paragraphs = table.textElement.querySelectorAll('p')
        if (paragraphs.length > 0) {
          paragraphs.forEach((p) => {
            const text = p.textContent?.trim()
            if (text) {
              content.push({ text, margin: [0, 0, 0, 5] })
            }
          })
        } else {
          const text = table.textContent
            .replace(/<[^>]*>/g, ' ')
            .replace(/\s+/g, ' ')
            .trim()
          if (text) {
            content.push({ text, margin: [0, 0, 0, 10] })
          }
        }
      }
    })
  }

  if (textHtml.length > 0) {
    textHtml.forEach((html) => {
      const textContent = html
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
      content.push({ text: textContent, margin: [0, 0, 0, 10] })
    })
  }

  if (signature) {
    content.push({ text: 'Electronic Signature', style: 'tableHeader', margin: [0, 10, 0, 5] })
    content.push({ text: signature, margin: [0, 0, 0, 10] })
  }

  const docDefinition: TDocumentDefinitions = {
    content,
    styles: {
      header: {
        fontSize: 18,
        bold: true,
      },
      subheader: {
        fontSize: 14,
        bold: true,
      },
      tableHeader: {
        fontSize: 12,
        bold: true,
      },
    },
    defaultStyle: {
      fontSize: 10,
    },
  }

  const fileName = `${metadata.title.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`
  pdfMake.createPdf(docDefinition).getBlob((blob) => {
    const url = URL.createObjectURL(blob)
    window.open(url, '_blank')
    const a = document.createElement('a')
    a.href = url
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  })
}
